import { useCallback } from 'react';
import { HStack, LinkText, Stack, Text } from '@adc/parallax-component-library';

import type { ModalName } from '~/features/modals/modals';
import { useModal } from '~/features/modals/useModal';
import { buildTestId } from '~/utils/buildTestId';

import { LactateLogoGradient } from '../LactateLogoGradient';

const TEST_ID = 'footer' as const;

type Item = { label: string; testIDSuffix: string[] | string } & (
  | { link: string }
  | { modal: ModalName }
);

const LEFT_ITEMS: Item[] = [
  { label: 'About', link: '#', testIDSuffix: 'about' },
  {
    label: 'Your Privacy Choices',
    link: '#',
    testIDSuffix: ['your', 'privacy', 'choices'],
  },
  {
    label: 'Privacy Notice',
    modal: 'PrivacyNotice',
    testIDSuffix: ['privacy', 'notice'],
  },
  { label: 'HIPAA', link: '#', testIDSuffix: 'hipaa' },
] as const;

const RIGHT_ITEMS: Item[] = [
  {
    label: 'Support and Resources',
    link: '#',
    testIDSuffix: ['support', 'and', 'resources'],
  },
  {
    label: 'Quick Start Guide',
    link: '#',
    testIDSuffix: ['quick', 'start', 'guide'],
  },
  {
    label: 'Terms of Use',
    modal: 'TermsOfUse',
    testIDSuffix: ['terms', 'of', 'use'],
  },
] as const;

export const Footer = () => {
  const { openModal } = useModal();

  const handleOnPressLeftItem = useCallback(
    (item: Item) => {
      if ('modal' in item) {
        openModal(item.modal);
      } else if (item.link) {
        window.open(item.link, '_blank');
      }
    },
    [openModal],
  );

  const openSettingsModal = useCallback(() => {
    openModal('Settings');
  }, [openModal]);

  return (
    <Stack testID={TEST_ID}>
      <Stack
        testID={buildTestId({ testID: TEST_ID, suffix: 'top' })}
        backgroundColor="$surface.brand"
        padding="$10"
        justifyContent="center"
      >
        <HStack alignItems="center" marginHorizontal={240}>
          <Stack flex={1}>
            <HStack>
              <LactateLogoGradient
                testID={buildTestId({ testID: TEST_ID, suffix: 'logo' })}
              />
              <Text
                type="$h3.strong"
                color="$neutral.noChangeWhite"
                testID={buildTestId({
                  testID: TEST_ID,
                  suffix: 'title',
                })}
              >
                Lactate
                <Text type="$h3.default" color="$neutral.noChangeWhite">
                  View
                </Text>
              </Text>
            </HStack>

            <HStack
              flex={1}
              marginHorizontal="$8"
              justifyContent="space-between"
              alignContent="flex-end"
              alignItems="flex-start"
              testID={buildTestId({ testID: TEST_ID, suffix: 'links' })}
            >
              <Stack
                marginTop={38}
                gap="$2"
                testID={buildTestId({ testID: TEST_ID, suffix: 'left' })}
              >
                {LEFT_ITEMS.map((item) => {
                  const { label, testIDSuffix } = item;

                  return (
                    <LinkText
                      key={label}
                      onPress={() => {
                        handleOnPressLeftItem(item);
                      }}
                      // @ts-expect-error TODO: Parallax is not exposing the type prop for LinkText
                      type="$bodyBase.medium"
                      theme="onBrand"
                      testID={buildTestId({
                        testID: TEST_ID,
                        suffix: testIDSuffix,
                      })}
                    >
                      {label}
                    </LinkText>
                  );
                })}
              </Stack>

              <Stack
                marginTop="$2"
                gap="$2"
                testID={buildTestId({ testID: TEST_ID, suffix: 'right' })}
              >
                <Text
                  // @ts-expect-error TODO: Parallax is not exposing color prop for Text
                  type="$bodyBase.regular"
                  color="$gray11Dark"
                  testID={buildTestId({
                    testID: TEST_ID,
                    suffix: ['healthcare', 'professionals', 'title'],
                  })}
                >
                  Healthcare Professionals
                </Text>
                {RIGHT_ITEMS.map((item) => {
                  const { label, testIDSuffix } = item;

                  return (
                    <LinkText
                      key={label}
                      onPress={() => {
                        handleOnPressLeftItem(item);
                      }}
                      // @ts-expect-error TODO: Parallax is not exposing the type prop for LinkText
                      type="$bodyBase.medium"
                      theme="onBrand"
                      testID={buildTestId({
                        testID: TEST_ID,
                        suffix: testIDSuffix,
                      })}
                    >
                      {label}
                    </LinkText>
                  );
                })}
                <LinkText
                  onPress={openSettingsModal}
                  // @ts-expect-error TODO: Parallax is not exposing the type prop for LinkText
                  type="$bodyBase.medium"
                  theme="onBrand"
                  testID={buildTestId({
                    testID: TEST_ID,
                    suffix: 'settings',
                  })}
                >
                  Settings
                </LinkText>
              </Stack>
            </HStack>
          </Stack>
        </HStack>
      </Stack>
      <Stack
        flex={1}
        alignItems="center"
        padding="$4"
        backgroundColor="$color.blue1Dark"
        testID={buildTestId({ testID: TEST_ID, suffix: 'bottom' })}
      >
        <Text
          type="$bodySmall.default"
          color="$gray11Dark"
          testID={buildTestId({ testID: TEST_ID, suffix: 'disclaimer' })}
        >
          The sensor housing, LactateView, Libre, and related brand marks are
          marks of Abbott. Other trademarks are the property of their respective
          owners.
        </Text>
      </Stack>
    </Stack>
  );
};
